
import { isSSR } from '../isSSR';

// Helper to get location permission status
export const checkGeolocationPermission = async (): Promise<string> => {
  // In SSR, skip permission check
  if (isSSR()) {
    return "prompt";
  }
  
  // If navigator is undefined, we're in a non-browser environment
  if (typeof navigator === 'undefined') {
    return "prompt";
  }
  
  // First check if geolocation is supported at all
  if (!navigator.geolocation) {
    console.log("Geolocation API not supported");
    return "denied";
  }
  
  // Check permissions API
  if (navigator.permissions) {
    try {
      const result = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
      console.log("Geolocation permission state:", result.state);
      return result.state;
    } catch (error) {
      console.error("Error checking geolocation permission:", error);
      
      // Fallback: try to detect if we can get position
      try {
        // Try to get position with a short timeout to check permissions
        await new Promise<GeolocationPosition>((resolve, reject) => {
          const timeoutId = setTimeout(() => {
            reject(new Error("Timeout checking geolocation permission"));
          }, 1000);
          
          navigator.geolocation.getCurrentPosition(
            (position) => {
              clearTimeout(timeoutId);
              resolve(position);
            },
            (error) => {
              clearTimeout(timeoutId);
              reject(error);
            },
            { timeout: 1000, maximumAge: 3600000 }
          );
        });
        
        // If we got here, permission is granted
        return "granted";
      } catch (posError) {
        // Check if permission was denied
        if (posError instanceof GeolocationPositionError && posError.code === 1) {
          return "denied";
        }
        
        // For other errors, assume prompt
        return "prompt";
      }
    }
  }
  
  // If permissions API is not available, return prompt state
  return "prompt";
};
